import { FieldErrors } from "react-hook-form";

/**
 * Nombre con el que cada campo aparece en el resumen de errores.
 *
 * La regla es que sea **el mismo texto que el guardia lee arriba del campo**, sin el asterisco de
 * obligatorio: el resumen sirve para ir a buscarlo en el formulario, y un nombre propio ("Marca de
 * vehículo" cuando la etiqueta dice "Marca") o el nombre interno del campo obligan a adivinar.
 * Si se cambia una etiqueta en el formulario, hay que cambiarla también acá.
 *
 * Las únicas entradas que no salen de una etiqueta son los campos que sólo anclan el error de una
 * sección entera —no hay un input con nombre al lado—: ahí va el título de la sección.
 */
export const FIELD_LABEL_MAP: Record<string, string> = {
  // Datos del visitante
  firstName: "Nombre",
  lastName: "Apellido",
  documentCountry: "País del documento",
  documentNumber: "Número de documento",
  email: "Mail",
  phone: "Número de celular",
  sex: "Género",
  birthdate: "Fecha de nacimiento",

  // Secciones sin etiqueta propia: el nombre es el título de la sección que hay que completar.
  selectedAccessMethod: "Tipo de autorización",
  authorizationSelected: "Autorización de acceso",
  // Distinto de `authorizationSelected` a propósito: compartir etiqueta hace imposible saber cuál
  // de los dos campos está faltando cuando aparece en el resumen de errores.
  transportAuthorizationSelected: "Autorización de transporte",
  vehicleType: "Medio de acceso",
  selectedVehicleId: "Vehículo",

  // Chofer (flujo de transportistas)
  driverLegajoNumber: "Número de legajo",
  driverLicenseExpirationDate: "Fecha de Vencimiento de Licencia",
  driverArtCompany: "Compañía de ART",
  driverArtPolicyNumber: "Número de Póliza ART",
  driverArtExpirationDate: "Fecha de Vencimiento ART",

  // Vehículo
  vehicleTypeId: "Tipo de vehículo",
  vehiclePlate: "Patente",
  vehicleBrand: "Marca",
  vehicleModel: "Modelo",
  vehicleColor: "Color",
  vtvExpirationDate: "Fecha de vencimiento VTV",
  vehicleInsuranceCompany: "Aseguradora",
  vehiclePolicyNumber: "Póliza n°",
  insuranceExpirationDate: "Fecha de vencimiento del seguro",

  // Vehículos asociados: mismos campos que el vehículo, con otros nombres internos.
  insuranceCompany: "Aseguradora",
  policyNumber: "Póliza n°",

  // Observaciones
  accessRequirementCheck: "Requisitos de acceso",
  transportNumeroRemito: "Número de remito",
  transportPrecinto: "Precinto",
  transportPallets: "Pallets / Bultos",
  transportProvincia: "Provincia",

  // Acompañantes
  companionName: "Nombre",
  companionLastName: "Apellido",
  companionDocumentCountry: "País del Documento",
  companionDocumentNumber: "Número de Documento",
  companionEmail: "Mail",
  companionCellphone: "Número de Celular",
  companionSex: "Género",
  companionBirthdate: "Fecha de nacimiento",
  companionArtCompany: "Compañía de ART",
  companionArtPolicyNumber: "Número de Póliza ART",
  companionArtExpirationDate: "Fecha de Vencimiento ART",
};

/**
 * Campos que viven dentro de una lista: el error llega como `<lista>.<índice>.<campo>` y sin el
 * número no se sabe cuál de las tarjetas hay que completar. El prefijo es el título de la tarjeta
 * —"Acompañante 2", "Vehículo Asociado 1"—, que es lo que el guardia ve en pantalla.
 */
const LIST_ITEM_TITLES: Record<string, string> = {
  companions: "Acompañante",
  trailerVehicles: "Vehículo Asociado",
};

export const getFieldLabel = (path: string): string => {
  const parts = path.split(".");
  const lastPart = parts[parts.length - 1];

  const listTitle = LIST_ITEM_TITLES[parts[0]];
  if (parts.length > 1 && listTitle) {
    const itemIndex = parseInt(parts[1], 10);
    const itemNumber = isNaN(itemIndex) ? "" : ` ${itemIndex + 1}`;

    if (lastPart === "authorizationSelected") {
      return `${listTitle}${itemNumber} - Autorización de acceso`;
    }
    const baseLabel = FIELD_LABEL_MAP[lastPart] || lastPart;
    return `${listTitle}${itemNumber} - ${baseLabel}`;
  }

  return FIELD_LABEL_MAP[path] || FIELD_LABEL_MAP[lastPart] || path;
};

export interface FlattenedError {
  name: string;
  label: string;
  message?: string;
}

export const flattenErrors = (errors: FieldErrors): FlattenedError[] => {
  const result: FlattenedError[] = [];

  const recurse = (obj: any, path: string = "") => {
    if (!obj || typeof obj !== "object") return;

    // Check if it's a leaf error node from react-hook-form
    // Error nodes have a `type` string and optionally `message` and `ref`
    if (obj.type && typeof obj.type === "string" && ("message" in obj || "ref" in obj)) {
      result.push({
        name: path,
        label: getFieldLabel(path),
        message: typeof obj.message === "string" ? obj.message : undefined,
      });
      return;
    }

    // Otherwise recurse through properties, skipping internal error properties
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      if (key === "ref" || key === "type" || key === "message" || key === "types") continue;
      const nextPath = path ? `${path}.${key}` : key;
      recurse(obj[key], nextPath);
    }
  };

  recurse(errors);
  return result;
};

/**
 * Busca el elemento que ancla el campo dentro de `scope`.
 *
 * `scope` es el formulario dueño del error y no es opcional por comodidad: todas las pestañas
 * abiertas —un ingreso y un egreso del mismo guardia, por ejemplo— viven a la vez en el DOM y la
 * inactiva sólo está tapada con `display: none` (ver RegistroIngreso.tsx), así que cada
 * `data-field-name` está repetido una vez por pestaña. Una búsqueda global se queda siempre con la
 * primera, que si no es la activa está oculta: ni el scroll ni el foco hacen nada y el botón
 * "Ver faltante" parecía no responder.
 */
const findFieldElement = (fieldName: string, scope: ParentNode): HTMLElement | null =>
  (scope.querySelector(`[data-field-name="${fieldName}"]`) as HTMLElement | null) ||
  // Los campos que no pasan por los wrappers con `data-field-name` se buscan por su atributo name.
  (scope.querySelector(`[name="${fieldName}"]`) as HTMLElement | null);

/**
 * Un campo puede estar dentro de varios acordeones anidados —la tarjeta de un acompañante dentro de
 * la sección de autorizaciones, por ejemplo— y basta con que uno esté cerrado para que quede
 * invisible: hay que abrir toda la cadena hacia arriba, no sólo el más cercano.
 *
 * Devuelve si hubo que abrir alguno, para saber cuánto esperar antes de medir el scroll.
 */
const expandAncestorAccordions = (element: Element): boolean => {
  let openedAny = false;
  let accordion: Element | null = element.closest(".MuiAccordion-root");

  while (accordion) {
    // El summary propio del acordeón viene antes que el de cualquiera anidado en sus detalles, así
    // que el primero que aparece en orden de documento es el que hay que clickear.
    const summary = accordion.querySelector(".MuiAccordionSummary-root") as HTMLElement | null;
    const isExpanded =
      !!summary &&
      (summary.classList.contains("Mui-expanded") ||
        summary.getAttribute("aria-expanded") === "true");

    if (summary && !isExpanded) {
      summary.click();
      openedAny = true;
    }

    accordion = accordion.parentElement
      ? accordion.parentElement.closest(".MuiAccordion-root")
      : null;
  }

  return openedAny;
};

const FOCUSABLE_SELECTOR =
  "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex='0']";

const FIELD_HIGHLIGHT_CLASS = "registroIngresoFieldHighlight";
const FIELD_HIGHLIGHT_DURATION_MS = 1000;

/**
 * Marca el campo con un flash temporal. Sin esto, avanzar entre varios errores de una misma
 * sección ya visible en pantalla (por ejemplo, 4 campos faltantes del vehículo, todos a la vista)
 * mueve el foco pero no se nota: no hay scroll porque ya está en viewport, y el `:focus` por
 * defecto de los inputs de Material-UI es demasiado sutil para notarlo entre un click y el
 * siguiente. El flash es la única señal de que el botón sí avanzó al próximo campo.
 */
const highlightField = (target: HTMLElement) => {
  target.classList.remove(FIELD_HIGHLIGHT_CLASS);
  // Fuerza el reflow: si el mismo campo se vuelve a marcar antes de que termine la animación
  // anterior, sin esto el navegador no la reinicia.
  void target.offsetWidth;
  target.classList.add(FIELD_HIGHLIGHT_CLASS);
  window.setTimeout(() => target.classList.remove(FIELD_HIGHLIGHT_CLASS), FIELD_HIGHLIGHT_DURATION_MS);
};

/**
 * Lleva la vista hasta el campo y le da el foco.
 *
 * Devuelve `false` cuando el campo no se puede alcanzar —su formulario no está montado, porque la
 * tarjeta que lo contiene se cerró después del submit y el error quedó igual (ver el `keepErrors`
 * al cancelar la edición de un vehículo)—. Los llamadores usan eso para no dejar el botón muerto y
 * seguir al próximo faltante que sí se pueda mostrar.
 */
export const focusField = (fieldName: string, scope?: HTMLElement | null): boolean => {
  const target = findFieldElement(fieldName, scope || document);

  // `offsetParent` nulo significa que el campo está dentro de algo con `display: none` y no se lo
  // puede mostrar. Un acordeón cerrado no cae acá: se oculta con `visibility`, y de eso se encarga
  // `expandAncestorAccordions`.
  if (!target || !target.offsetParent) return false;

  const opened = expandAncestorAccordions(target);

  // Un timeout para que React alcance a renderizar el acordeón abierto y arranque la transición de
  // CSS: sin eso `scrollIntoView` mide el layout viejo y deja el campo fuera de la vista.
  setTimeout(
    () => {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      highlightField(target);

      const focusable = target.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null;
      (focusable || target).focus();
    },
    opened ? 250 : 50
  );

  return true;
};
